import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api.js";
import { getLandingVariant } from "../lib/abVariant.js";
import { LANDING_COPY } from "../lib/landingCopy.js";
import { trackMagicLinkRequested, trackVariantAssigned } from "../lib/analytics.js";

export function Landing() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const variant = useMemo(() => getLandingVariant(), []);
  const copy = LANDING_COPY[variant];

  useEffect(() => {
    trackVariantAssigned(variant);
  }, [variant]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.requestMagicLink(email, variant);
      trackMagicLinkRequested(variant);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  if (submitted) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-stone-50 px-6 py-12">
        <div className="max-w-md text-center">
          <h1 className="text-3xl font-semibold tracking-tight">Check your email</h1>
          <p className="mt-3 text-stone-600">
            We sent a sign-in link to <span className="font-medium">{email}</span>. Tap the link
            to continue. The link expires in 15 minutes.
          </p>
          <p className="mt-6 text-sm text-stone-500">
            Didn't get it? Check spam, or{" "}
            <button
              type="button"
              onClick={() => setSubmitted(false)}
              className="underline text-stone-700 hover:text-stone-900"
            >
              try a different email
            </button>
            .
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-stone-50">
      {/* Hero — problem first */}
      <section className="px-6 pt-12 pb-10 sm:pt-16">
        <div className="mx-auto max-w-5xl">
          <p className="text-sm font-medium uppercase tracking-widest text-stone-500">
            {copy.eyebrow}
          </p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tight text-stone-900 leading-tight max-w-3xl">
            {copy.headline}
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-stone-700 leading-relaxed">
            {copy.subhead}
          </p>
        </div>
        {/* Full-bleed hero illustration (~21:9). Watercolor + ink line; diverse hands
            converging texts into a shared calendar grid — the resolution Inkling provides. */}
        <div className="mx-auto max-w-6xl mt-10">
          <picture>
            <source srcSet="/inkling-hero.webp" type="image/webp" />
            <img
              src="/inkling-hero.jpg"
              alt="A top-down view of a kitchen table covered in everyday family chaos — a school flyer, sticky notes, a coffee mug, a succulent, several phones — with diverse hands at the edges sending text bubbles that converge into a softly glowing shared calendar grid at the center."
              className="w-full h-auto rounded-2xl shadow-sm ring-1 ring-stone-200/70"
              loading="eager"
              decoding="async"
            />
          </picture>
        </div>
        <div className="mx-auto max-w-5xl mt-12">
          <p className="max-w-2xl text-base text-stone-600 leading-relaxed">
            {copy.inclusion}
          </p>
          <form onSubmit={onSubmit} className="mt-8 max-w-md">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="flex-1 rounded-lg border border-stone-300 bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-stone-900"
              />
              <button
                type="submit"
                disabled={busy}
                className="rounded-lg bg-stone-900 text-white px-6 py-3 font-medium hover:bg-stone-800 transition disabled:opacity-50"
              >
                {busy ? "Sending…" : copy.cta}
              </button>
            </div>
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            <p className="mt-3 text-xs text-stone-500">
              No password to forget, no app to install. We'll email you a sign-in link.
            </p>
          </form>
          <p className="mt-6 max-w-md text-sm text-stone-600">
            Prefer not to type an email?{" "}
            <a href="sms:+18778731681" className="font-medium text-stone-900 underline hover:text-stone-700">
              Text "Hi" to (877) 873-1681
            </a>{" "}
            and we'll set up your family right there in the thread — no email required.
          </p>
        </div>
      </section>

      {/* Three pillars */}
      <section className="px-6 py-14 sm:py-20 border-t border-stone-200">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">
            How it works in three texts
          </h2>
          <div className="mt-12 grid gap-12 sm:grid-cols-3 sm:gap-10">
            <Pillar
              eyebrow="01 · Text"
              title="Text the family number anything you want on the calendar."
              body="Soccer practice tomorrow at 4pm. A photo of a school flyer. A voice note while you're walking the dog. We read it all the same way."
              imgSrc="/inkling-pillar-text"
              imgAlt="A hand holding a phone with three speech bubbles rising — one filled with abstract handwriting, one holding a watercolor sketch of a school flyer, one holding an audio waveform — three ways to text the same calendar."
            />
            <Pillar
              eyebrow="02 · Confirm"
              title="We confirm if we're sure, ask if we're not."
              body="High-confidence parses go straight on the calendar with a confirmation text. Anything ambiguous gets a single short clarifying question, never an interrogation."
              imgSrc="/inkling-pillar-confirm"
              imgAlt="A phone screen showing two stacked watercolor speech bubbles — a peach outgoing message above, and a sage reply bubble below containing a small calendar-grid icon with a checkmark — a calm exchange, never an interrogation."
            />
            <Pillar
              eyebrow="03 · See"
              title="Everyone in your circle sees the calendar."
              body="Open the web app, see the week. Tap any event to see who added it and the original message that created it. Sync to your Google Calendar if you want."
              imgSrc="/inkling-pillar-see"
              imgAlt="A softly-glowing watercolor calendar grid at the center with hearts and flowers in a few cells, surrounded by four diverse hands holding phones — a tattooed wrist, an older hand with a ring, a manicured hand with a bracelet, a hand in a chunky knit sleeve — each phone reflecting the same calendar."
            />
          </div>
        </div>
      </section>

      {/* Inclusion */}
      <section className="px-6 py-14 border-t border-stone-200 bg-white">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">Family means whatever it means to you</h2>
          <p className="mt-4 max-w-3xl text-stone-700 leading-relaxed">
            Most calendar apps were built for a specific shape of household — two parents,
            shared address, predictable schedules. The actual people who need shared calendars
            don't fit that shape. Inkling is built for the people who actually do the
            coordinating, in whatever configuration that is.
          </p>
          <ul className="mt-10 space-y-10">
            <ShapeItem
              imgSrc="/inkling-shape-coparent"
              imgAlt="Two stylized houses — one in sage green wash, one in peach — facing each other with a child's backpack mid-arc between them, suggesting one kid traveling between two homes."
              title="Co-parenting across two households."
              body="Each household runs its own Inkling family; you can also add the other parent as a member if you want one shared calendar across both homes."
            />
            <ShapeItem
              imgSrc="/inkling-shape-chosen"
              imgAlt="A round table seen from above with five mismatched coffee mugs and a set of housekeys in the middle, four hands of varied skin tones reaching in from the edges — chosen family gathered around."
              title="Chosen family and friend groups."
              body="Roommates, found family, the people you trade kid pickups with — Inkling doesn't ask how you're related."
            />
            <ShapeItem
              imgSrc="/inkling-shape-multigen"
              imgAlt="Three pairs of hands stacked — a child's small hands on top, an adult's hands in the middle, an elder's wrinkled hands at the base — together cradling a single shared calendar page."
              title="Multi-generational households."
              body="Grandparents, parents, kids, cousins. As many family members as you want; the calendar shows everyone what's happening."
            />
            <ShapeItem
              imgSrc="/inkling-shape-couple"
              imgAlt="A sunlit windowsill with two coffee mugs side by side, a houseplant in a terra-cotta pot, a folded newspaper, and a single dog leash hanging off the edge — two adults sharing a quiet morning."
              title="Couples without kids."
              body="Two adults sharing schedules works exactly the same way."
            />
            <ShapeItem
              imgSrc="/inkling-shape-blended"
              imgAlt="A close-up of a patchwork quilt with eight different fabric squares — gingham, floral, polka-dot, plaid, striped — stitched together with visible hand-done stitching, every shape held together as one."
              title="De facto, separated, blended, single-parent."
              body="Whatever your household actually is, that's what Inkling supports."
            />
          </ul>
          <p className="mt-10 text-stone-600">
            <Link to="/how-it-works" className="underline text-stone-900 hover:text-stone-700">
              Read the longer story of how Inkling works →
            </Link>
          </p>
        </div>
      </section>

      {/* Capabilities */}
      <section className="px-6 py-12 border-t border-stone-200">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-semibold tracking-tight">What you can text in</h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-2">
            <Capability
              title="Plain text"
              example="“Dentist Wednesday at 3:15 downtown”"
              note="The most common case. Time, place, who — Inkling pulls it out and puts it on the calendar."
              imgSrc="/inkling-cap-text"
              imgAlt="A peach watercolor speech bubble filled with abstract handwriting scribbles, with a sage sprig curling beside it."
            />
            <Capability
              title="Photos and screenshots"
              example="A photo of a flyer, a swim-meet schedule, a class email"
              note="Inkling reads the image. Multiple events on one flyer? It can pull out all of them in a single confirmation."
              imgSrc="/inkling-cap-photo"
              imgAlt="A speech bubble containing a watercolor sketch of a school flyer with a small schoolhouse, dates, and a heart."
            />
            <Capability
              title="Voice notes"
              example="“Pick up the kids at 3:30, then dentist at 4:15 downtown”"
              note="Hold the mic in Messages, send the voice note. Inkling transcribes and parses."
              imgSrc="/inkling-cap-voice"
              imgAlt="A dusty lavender watercolor speech bubble with an audio waveform inside, soft pastel ripple arcs spreading outward."
            />
            <Capability
              title="Inline corrections"
              example="“No, that's at 5pm not 4”"
              note="Reply to a confirmation within ten minutes and Inkling updates the event you just added."
              imgSrc="/inkling-cap-correct"
              imgAlt="Two stacked watercolor speech bubbles — peach with an ink strike-through scribble above, sage with a corrected scribble and a calendar-cell-with-check below — connected by a curving pastel arrow."
            />
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="px-6 py-12 border-t border-stone-200 bg-white">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-2xl font-semibold tracking-tight">Pricing</h2>
          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            <div className="rounded-2xl border border-stone-200 p-6">
              <p className="text-sm font-medium uppercase tracking-widest text-stone-500">Free</p>
              <p className="mt-3 text-3xl font-semibold">$0</p>
              <ul className="mt-4 space-y-2 text-sm text-stone-700">
                <li>Up to 3 family members</li>
                <li>50 events / month</li>
                <li>Text, photo, and voice input</li>
                <li>Web calendar</li>
              </ul>
            </div>
            <div className="rounded-2xl border border-stone-900 p-6 bg-stone-900 text-white">
              <p className="text-sm font-medium uppercase tracking-widest text-stone-300">Family</p>
              <p className="mt-3 text-3xl font-semibold">$29.99 / mo</p>
              <ul className="mt-4 space-y-2 text-sm text-stone-200">
                <li>Unlimited family members</li>
                <li>Unlimited events</li>
                <li>Google Calendar sync (per-family + per-member)</li>
                <li>Phase 2 features as they ship</li>
              </ul>
            </div>
          </div>
          <p className="mt-6 text-sm text-stone-500">
            Paid families can cancel any time from Settings. Free families never get nagged
            until you hit the monthly event limit.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-10 border-t border-stone-200 bg-stone-50">
        <div className="mx-auto max-w-5xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm text-stone-500">
          <p>© {new Date().getFullYear()} Tall Poppy Group, LLC</p>
          <nav className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/how-it-works" className="hover:text-stone-900">How it works</Link>
            <Link to="/sms-consent" className="hover:text-stone-900">SMS terms</Link>
            <a className="hover:text-stone-900" href="mailto:hello@inkling.family">
              hello@inkling.family
            </a>
          </nav>
        </div>
      </footer>
    </main>
  );
}

function Pillar({
  eyebrow,
  title,
  body,
  imgSrc,
  imgAlt,
}: {
  eyebrow: string;
  title: string;
  body: string;
  imgSrc?: string;
  imgAlt?: string;
}) {
  return (
    <div className="flex flex-col">
      {imgSrc && (
        <div className="mb-6 overflow-hidden rounded-xl bg-stone-50 ring-1 ring-stone-200/70">
          <picture>
            <source srcSet={`${imgSrc}.webp`} type="image/webp" />
            <img
              src={`${imgSrc}.jpg`}
              alt={imgAlt}
              className="w-full h-auto block"
              loading="lazy"
              decoding="async"
            />
          </picture>
        </div>
      )}
      <p className="text-xs font-medium uppercase tracking-widest text-stone-500">{eyebrow}</p>
      <h3 className="mt-3 text-lg font-semibold tracking-tight text-stone-900">{title}</h3>
      <p className="mt-2 text-stone-700 leading-relaxed">{body}</p>
    </div>
  );
}

function ShapeItem({
  imgSrc,
  imgAlt,
  title,
  body,
}: {
  imgSrc: string;
  imgAlt: string;
  title: string;
  body: string;
}) {
  return (
    <li className="flex flex-col sm:flex-row gap-5 sm:gap-6 items-start">
      <picture className="shrink-0">
        <source srcSet={`${imgSrc}.webp`} type="image/webp" />
        <img
          src={`${imgSrc}.jpg`}
          alt={imgAlt}
          className="block w-28 h-28 sm:w-32 sm:h-32 rounded-xl ring-1 ring-stone-200/70"
          loading="lazy"
          decoding="async"
        />
      </picture>
      <div className="flex-1">
        <h3 className="text-lg font-semibold tracking-tight text-stone-900">{title}</h3>
        <p className="mt-2 text-stone-700 leading-relaxed">{body}</p>
      </div>
    </li>
  );
}

function Capability({
  title,
  example,
  note,
  imgSrc,
  imgAlt,
}: {
  title: string;
  example: string;
  note: string;
  imgSrc?: string;
  imgAlt?: string;
}) {
  return (
    <div className="rounded-2xl border border-stone-200 p-6 bg-stone-50 flex gap-5 items-start">
      {imgSrc && (
        <picture className="shrink-0">
          <source srcSet={`${imgSrc}.webp`} type="image/webp" />
          <img
            src={`${imgSrc}.jpg`}
            alt={imgAlt}
            className="block w-20 h-20 sm:w-24 sm:h-24 object-contain"
            loading="lazy"
            decoding="async"
          />
        </picture>
      )}
      <div className="min-w-0 flex-1">
        <h3 className="text-base font-semibold tracking-tight text-stone-900">{title}</h3>
        <p className="mt-2 text-sm font-mono text-stone-600 leading-snug">{example}</p>
        <p className="mt-3 text-sm text-stone-700 leading-relaxed">{note}</p>
      </div>
    </div>
  );
}
